import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../App";
import { canManagePayments, isDebtAssignedToUser, isLoanOfficer } from "../accessControl";
import { callApi, formatDate, formatMoney, getInlineLoader } from "../Helpers";
import {
  MdCalendarToday,
  MdEdit,
  MdOutlinePayments,
  MdPhone,
  MdReceiptLong,
  MdTrendingDown,
  MdTrendingUp,
} from "react-icons/md";
import PaymentReceipt from "./PaymentReceipt";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [data, setData] = useState(null);
  const [activeHistoryTab, setActiveHistoryTab] = useState("payments");
  const [selectedPaymentReceipt, setSelectedPaymentReceipt] = useState(null);
  const [paymentDetailsMap, setPaymentDetailsMap] = useState({});
  const [ledgerExpanded, setLedgerExpanded] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  async function getData() {
    await callApi("get-debt-data", { uuid }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch data");
      }
    });
  }

  function handleCancelDebt() {
    appContext.activateDialog({
      message: "Are you sure you want to cancel this debt?",
      onConfirm: async () => {
        setIsCancelling(true);
        try {
          const response = await callApi("cancel-debt", { debtUuid: data.uuid });
          if (response.status === 1) {
            if (appContext.tellSuccess) appContext.tellSuccess("Debt successfully cancelled");
            getData(); 
          } else {
            appContext.tellError(response.msg || "Failed to cancel debt");
          }
        } catch (error) {
          console.error(error);
          appContext.tellError("A server error occurred while cancelling.");
        } finally {
          setIsCancelling(false);
        }
      }
    });
  }

  const getScheduleStatusClass = (status) => {
    const normalizedStatus = String(status || "").toLowerCase();
    if (normalizedStatus === "paid") return "ledger-status-paid";
    if (normalizedStatus === "partial") return "ledger-status-partial";
    if (normalizedStatus === "overdue") return "ledger-status-overdue";
    return "ledger-status-pending";
  };

  useEffect(() => {
    async function init() {
      setReady(false);
      await Promise.all([
        getData(),
        appContext.allPayments ? Promise.resolve() : appContext.getAllPayments(),
        appContext.allDebts ? Promise.resolve() : appContext.getAllDebts(),
        appContext.allSales ? Promise.resolve() : appContext.getAllSales(),
      ]);
      setReady(true);
    }
    init();
  }, [uuid]);

  useEffect(() => {
    if (data && isLoanOfficer(appContext.currentUserData) && !isDebtAssignedToUser(data, appContext.currentUserData)) {
      appContext.tellError("You can only view debts assigned to you.");
      appContext.navTo({ item: "debts" });
    }
  }, [data, appContext.currentUserData]);

  const showPaymentAction = canManagePayments(appContext.currentUserData);
  const schedules = data?.schedules || [];
  const ledgerPreviewCount = 4;
  const visibleSchedules = ledgerExpanded ? schedules : schedules.slice(0, ledgerPreviewCount);
  const ledgerHasMore = schedules.length > ledgerPreviewCount;
  const totalExpected = schedules.reduce((sum, item) => sum + Number(item.expectedAmount || 0), 0);
  const totalScheduledPaid = schedules.reduce((sum, item) => sum + Number(item.amountPaid || 0), 0);
  const paidSchedules = schedules.filter((item) => String(item.status || "").toLowerCase() === "paid").length;
  const customerName = data?.customerName || data?.fullname || "Customer";

  const relatedPayments = useMemo(() => {
    const allPayments = appContext.allPayments || [];
    const debtKeys = getDebtMatchKeys(data);

    return allPayments
      .filter((item) => isRelatedPayment(item, data, debtKeys))
      .sort((first, second) => sortByDateDesc(first?.dateCreated || first?.date, second?.dateCreated || second?.date));
  }, [appContext.allPayments, data]);

  const relatedDebts = useMemo(() => {
    const allDebts = appContext.allDebts || [];

    return allDebts
      .filter((item) => isRelatedDebt(item, data))
      .sort((first, second) => sortByDateDesc(first?.dateCreated || first?.date, second?.dateCreated || second?.date));
  }, [appContext.allDebts, data]);

  const relatedSales = useMemo(() => {
    const allSales = appContext.allSales || [];
    return allSales.filter((item) => isRelatedSale(item, data));
  }, [appContext.allSales, data]);

  useEffect(() => {
    async function loadPaymentDetails() {
      if (!relatedPayments.length) return;
      const missingPayments = relatedPayments.filter((item) => item?.uuid && !paymentDetailsMap[item.uuid]);
      if (missingPayments.length === 0) return;

      const entries = await Promise.all(
        missingPayments.map(async (item) => {
          const response = await callApi("get-payment-data", { uuid: item.uuid });
          if (response.status === 1 && response.data) {
            return [item.uuid, response.data];
          }
          return [item.uuid, item];
        })
      );

      setPaymentDetailsMap((current) => {
        const next = { ...current };
        entries.forEach(([key, value]) => {
          next[key] = value;
        });
        return next;
      });
    }
    loadPaymentDetails();
  }, [relatedPayments, paymentDetailsMap]);

  const paymentHistoryTitle = `${customerName} Payment History`;
  const debtHistoryTitle = `${customerName} Debt History`;
  const accountSummaryDuration = resolveDebtDurationLabel(data, relatedSales);

  if (!ready || !data) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  if (isLoanOfficer(appContext.currentUserData) && !isDebtAssignedToUser(data, appContext.currentUserData)) return null;

  function openReceipt(payment) {
    setSelectedPaymentReceipt(payment);
  }

  return (
    <div className="container">
      <div className="detail-shell">
        
        {/* REFACTORED RESPONSIVE HEADER SECTION */}
        <div className="detail-header d-flex flex-column align-items-center align-items-md-center justify-content-between gap-3 pb-3 border-bottom">
          <div className="text-center">
            <p className="app-page-eyebrow mb-1 text-uppercase tracking-wider small text-muted">Debt account</p>
            <h4 className="main-section-title mb-2 font-bold">{customerName}</h4>
            <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap text-muted small">
              <div className="d-flex justify-content-center gap-1">
                <span className={`status-pill ${data.status === "cancelled" ? "status-pill-warning" : "status-pill-success"}`}>
                  {data.status || "active"}
                </span>
                <span className="status-pill status-pill-primary">{data.repaymentType || "Repayment plan"}</span>
              </div>
            </div>
          </div>

          {/* RESPONSIVE BUTTON CONTAINER SLOT */}
          
        </div>

        <div className="app-form-summary-grid mt-4">
          <div className="app-form-summary-card">
            <span>Total Contract</span>
            <strong>{formatMoney(data.totalAmount || 0)}</strong>
          </div>
          <div className="app-form-summary-card">
            <span>Collected</span>
            <strong className="text-success">{formatMoney(data.amountPaid || 0)}</strong>
          </div>
          <div className="app-form-summary-card">
            <span>Outstanding</span>
            <strong className="text-secondary">{formatMoney(data.balance || 0)}</strong>
          </div>
        </div>

        <div className="detail-actions-card mt-4">
          <h6 className="card-title mb-4 font-bold">Account Summary</h6>
          <div className="row">
            <div className="col-md-6 mb-4">
              <label className="form-label text-dark font-semi-bold">Customer Phone</label>
              <h6 className="text-muted d-flex align-items-center gap-2 mb-0">
                <MdPhone size={16} />
                <span>{data.phone || "Not set"}</span>
              </h6>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-label text-dark font-semi-bold">Repayment Type</label>
              <h6 className="text-muted d-flex align-items-center gap-2 mb-0">
                <MdReceiptLong size={16} />
                <span>{data.repaymentType || "Not set"}</span>
              </h6>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-label text-dark font-semi-bold">Contract Date</label>
              <h6 className="text-muted d-flex align-items-center gap-2 mb-0">
                <MdCalendarToday size={16} />
                <span>{data.contractDate || data.contractDateString || formatDate(data.dateCreated) || "Not set"}</span>
              </h6>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-label text-dark font-semi-bold">Due Date</label>
              <h6 className="text-muted d-flex align-items-center gap-2 mb-0">
                <MdCalendarToday size={16} />
                <span>{data.dueDate || "Not set"}</span>
              </h6>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-label text-dark font-semi-bold">Duration</label>
              <h6 className="text-muted d-flex align-items-center gap-2 mb-0">
                <MdReceiptLong size={16} />
                <span>{accountSummaryDuration}</span>
              </h6>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-label text-dark font-semi-bold">Installments Cleared</label>
              <h6 className="text-muted d-flex align-items-center gap-2 mb-0">
                <MdOutlinePayments size={16} />
                <span>{paidSchedules} of {schedules.length}</span>
              </h6>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2 w-100 w-md-auto mt-2 mt-md-0 app-header-actions-wrapper">
              {data.status !== "cancelled" && (
                <button
                  className="btn btn-danger btn-rounded app-cta-btn flex-fill flex-md-grow-0"
                  onClick={handleCancelDebt}
                  disabled={isCancelling}
                >
                  {isCancelling ? "Cancelling..." : "Cancel Debt"}
                </button>
              )}

              {showPaymentAction && data.status !== "cancelled" && (
                <>
                  <button
                    className="btn btn-primary btn-rounded app-cta-btn flex-fill flex-md-grow-0"
                    onClick={() => appContext.navTo({ item: "side-view", subItem: "edit-debt", extraItem: data.uuid })}
                  >
                    <MdEdit size={18} /> Edit Debt
                  </button>

                  <button
                    className="btn btn-dark btn-rounded app-cta-btn flex-fill flex-md-grow-0"
                    onClick={() => appContext.navTo({ item: "view", subItem: "create-payment", extraItem: data.uuid })}
                  >
                    <MdOutlinePayments size={18} /> Make payment
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        

        {/* Repayment Ledger Render Block */}
        {schedules.length > 0 ? (
          <div className="detail-actions-card mt-4">
            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
              <div>
                <h6 className="card-title mb-1 font-bold">Repayment Ledger</h6>
                <p className="text-muted small mb-0">
                  {ledgerExpanded
                    ? "Track every scheduled installment with clearer progress, payment totals, and status."
                    : `Preview the first ${Math.min(ledgerPreviewCount, schedules.length)} weeks, then expand to view the full record.`}
                </p>
              </div>

              <div className="d-flex align-items-end gap-2 flex-wrap">
                <div className="entity-card-chip-row">
                  <span className="status-pill status-pill-primary">{formatMoney(totalExpected)} scheduled</span>
                  <span className="status-pill status-pill-success">{formatMoney(totalScheduledPaid)} paid</span>
                  <span className="status-pill status-pill-warning">{data.officerName}</span>
                </div>

                {ledgerHasMore ? (
                  <button
                    className="btn btn-sm btn-outline-dark btn-rounded"
                    type="button"
                    onClick={() => setLedgerExpanded((current) => !current)}
                  >
                    {ledgerExpanded ? "Collapse record" : `View full record (${schedules.length})`}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table debt-ledger-table mb-0">
                <thead>
                  <tr>
                    <th>Installment</th>
                    <th className="text-end">Target</th>
                    <th className="text-end">Paid</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSchedules.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <div className="debt-ledger-main-cell">
                          <strong>Week {item.weekNumber}</strong>
                          <small>{item.dueDate || "Not set"}</small>
                        </div>
                      </td>
                      <td className="text-end">{formatMoney(item.expectedAmount || 0)}</td>
                      <td className="text-end fw-bold">{formatMoney(item.amountPaid || 0)}</td>
                      <td className="text-center">
                        <span className={`debt-ledger-status ${getScheduleStatusClass(item.status)}`}>
                          {String(item.status || "pending").toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="2">TOTAL</td>
                    <td className="text-end">{formatMoney(totalExpected)}</td>
                    <td className="text-end">{formatMoney(totalScheduledPaid)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : (
          <div className="empty-state-lite mt-4">No repayment schedule has been generated for this debt yet.</div>
        )}

        {/* Dynamic Tab History Stack */}
        <div className="detail-actions-card mt-4">
          {selectedPaymentReceipt ? (
            <>
              <button className="btn btn-secondary mb-3" onClick={() => setSelectedPaymentReceipt(null)}>
                &larr; Back to History
              </button>
              <div className="d-flex justify-content-center">
                <PaymentReceipt paymentData={selectedPaymentReceipt} />
              </div>
            </>
          ) : (
            <>
              <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                <div>
                  <h6 className="card-title mb-1">{activeHistoryTab === "payments" ? paymentHistoryTitle : debtHistoryTitle}</h6>
                  <p className="text-muted small mb-0">
                    {activeHistoryTab === "payments"
                      ? "Review all repayments recorded for this customer."
                      : "Review the debt account history and its repayment duration at a glance."}
                  </p>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <button
                    className={`btn btn-sm btn-rounded ${activeHistoryTab === "payments" ? "btn-success" : "btn-outline-dark"}`}
                    onClick={() => setActiveHistoryTab("payments")}
                    type="button"
                  >
                    Payment History
                  </button>
                  <button
                    className={`btn btn-sm btn-rounded ${activeHistoryTab === "debts" ? "btn-success" : "btn-outline-dark"}`}
                    onClick={() => setActiveHistoryTab("debts")}
                    type="button"
                  >
                    Debt History
                  </button>
                </div>
              </div>

              {activeHistoryTab === "payments" ? (
                relatedPayments.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table debt-ledger-table mb-0">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="text-end">Amount</th>
                          <th className="text-end">Balance</th>
                          <th>Issued By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatedPayments.map((item, index) => (
                          <tr key={item.uuid || index} style={{ cursor: "pointer" }} onClick={() => openReceipt(paymentDetailsMap[item.uuid] || item)}>
                            <td>
                              <div className="debt-ledger-main-cell">
                                <strong>{formatDate(item.dateCreated || item.date) || "Not set"}</strong>
                                <small>{item.model || "Motorcycle not set"}</small>
                              </div>
                            </td>
                            <td className="text-end fw-bold">{formatMoney(item.amount || 0)}</td>
                            <td className="text-end">{formatMoney(item.balance || 0)}</td>
                            <td>{resolvePaymentIssuedBy(item, paymentDetailsMap[item.uuid])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state-lite">No payment history was found for this customer.</div>
                )
              ) : relatedDebts.length > 0 ? (
                <div className="table-responsive">
                  <table className="table debt-ledger-table mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-end">Total Contract</th>
                        <th className="text-end">Collected</th>
                        <th className="text-end">Balance</th>
                        <th>Duration</th>
                        <th>Officer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relatedDebts.map((item, index) => (
                        <tr key={item.uuid || index}>
                          <td>
                            <div className="debt-ledger-main-cell">
                              <strong>{formatDate(item.dateCreated || item.date) || "Not set"}</strong>
                              <small>{item.repaymentType || "Repayment plan"}</small>
                            </div>
                          </td>
                          <td className="text-end fw-bold">{formatMoney(item.totalAmount || 0)}</td>
                          <td className="text-end">{formatMoney(item.amountPaid || 0)}</td>
                          <td className="text-end">{formatMoney(item.balance || 0)}</td>
                          <td>{resolveDebtDurationLabel(item, relatedSales)}</td>
                          <td>{item.officerName || item.issuedByName || "System"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state-lite">No debt history was found for this customer.</div>
              )}
            </>
          )}
        </div>

      </div>
    </div>
  );
}

function getDebtMatchKeys(data) {
  return [
    data?.uuid,
    data?.id,
    data?.debtUuid,
    data?.debtID,
    data?.debtId,
    data?.customerName,
    data?.fullname,
    data?.phone,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
}

function isRelatedPayment(payment, debt, debtKeys) {
  const paymentKeys = [
    payment?.debtUuid,
    payment?.debtId,
    payment?.debtID,
    payment?.debt,
    payment?.customerName,
    payment?.fullname,
    payment?.phone,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  if (paymentKeys.some((key) => debtKeys.includes(key))) {
    return true;
  }

  const paymentName = String(payment?.fullname || payment?.customerName || "").trim().toLowerCase();
  const debtName = String(debt?.customerName || debt?.fullname || "").trim().toLowerCase();
  const paymentPhone = String(payment?.phone || "").trim().toLowerCase();
  const debtPhone = String(debt?.phone || "").trim().toLowerCase();

  return (paymentName && debtName && paymentName === debtName) || (paymentPhone && debtPhone && paymentPhone === debtPhone);
}

function isRelatedDebt(item, debt) {
  const itemName = String(item?.customerName || item?.fullname || "").trim().toLowerCase();
  const debtName = String(debt?.customerName || debt?.fullname || "").trim().toLowerCase();
  const itemPhone = String(item?.phone || "").trim().toLowerCase();
  const debtPhone = String(debt?.phone || "").trim().toLowerCase();

  if (String(item?.uuid || "").toLowerCase() === String(debt?.uuid || "").toLowerCase()) {
    return true;
  }

  return (itemName && debtName && itemName === debtName) || (itemPhone && debtPhone && itemPhone === debtPhone);
}

function resolveDebtDurationLabel(debt, sales) {
  const durationFromDebt =
    debt?.saleDurationMonths ||
    debt?.durationMonths ||
    debt?.loanDuration ||
    debt?.duration ||
    debt?.repaymentMonths;

  if (durationFromDebt) {
    return `${durationFromDebt} month(s)`;
  }

  if (sales && sales.length > 0) {
    const matchedSale = sales.find((sale) => isRelatedSale(sale, debt));
    const durationFromSale = matchedSale?.durationMonths || matchedSale?.loanDuration || matchedSale?.duration;
    if (durationFromSale) {
      return `${durationFromSale} month(s)`;
    }
  }

  return "Not set";
}

function resolvePaymentIssuedBy(item, detailedItem) {
  return (
    detailedItem?.issuedByName ||
    detailedItem?.authorizerName ||
    detailedItem?.sellerName ||
    item?.issuedByName ||
    item?.authorizerName ||
    item?.sellerName ||
    item?.officerName ||
    "System Authorized"
  );
}

function isRelatedSale(item, debt) {
  const itemName = String(item?.fullname || "").trim().toLowerCase();
  const debtName = String(debt?.customerName || debt?.fullname || "").trim().toLowerCase();
  const itemPhone = String(item?.phone || "").trim().toLowerCase();
  const debtPhone = String(debt?.phone || "").trim().toLowerCase();
  const itemModel = String(item?.model || "").trim().toLowerCase();
  const debtModel = String(debt?.model || "").trim().toLowerCase();

  const directKeyMatch = [
    item?.debtId,
    item?.debtID,
    item?.debtUuid,
  ]
    .filter(Boolean)
    .some((value) => {
      const normalizedValue = String(value).toLowerCase();
      return [
        debt?.uuid,
        debt?.id,
        debt?.debtUuid,
        debt?.debtID,
        debt?.debtId,
      ]
        .filter(Boolean)
        .map((entry) => String(entry).toLowerCase())
        .includes(normalizedValue);
    });

  return directKeyMatch || ((itemName && debtName && itemName === debtName) && (itemModel ? itemModel === debtModel : true)) || (itemPhone && debtPhone && itemPhone === debtPhone);
}

function sortByDateDesc(first, second) {
  return parseDateValue(second).getTime() - parseDateValue(first).getTime();
}

function parseDateValue(value) {
  if (!value && value !== 0) return new Date("invalid");
  if (value instanceof Date) return value;

  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    const numericValue = Number(value);
    return new Date(String(Math.abs(numericValue)).length <= 10 ? numericValue * 1000 : numericValue);
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  return new Date(value);
}